import { useEffect, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import { Badge, Button, Callout, Icon, IconButton, cn } from "@winche/ui";
import { Block, ErrorMessage, Panel, SectionHeader } from "../components/ui";
import { describeCallableError, type AdminFailure } from "../lib/callable-error";
import { DataTable, type Column } from "../components/table";
import { ModelSearch, type OrModel } from "../components/ModelSearch";
import { ModelChangeDialog, ModelChangeNotice } from "../components/ModelChangeDialog";
import type { ModelChange } from "../lib/model-change";
import { isFreeModelId } from "../lib/or-models";
import { createLatestSaver, type LatestSaver } from "../lib/latest-save";
import { modelPricePer1M, type OperationStat, type AiOperation } from "@winche/shared";

// ─── Types ────────────────────────────────────────────────────────────────────

interface BenchmarkOp {
  code: AiOperation;
  label: string;
  fallbackInput: number;
  fallbackOutput: number;
  isVision?: boolean;
}

interface Model {
  id: string;
  label: string;
  /** USD per million tokens; null when nothing prices the model ("price unknown"). */
  inputPer1M: number | null;
  outputPer1M: number | null;
  isCustom?: boolean;
  supportsVision?: boolean;
}

interface ModelLists {
  text: Model[];
  vision: Model[];
}

// ─── Static data ─────────────────────────────────────────────────────────────

const TEXT_OPERATIONS: BenchmarkOp[] = [
  { code: "parse_cv",           label: "Parse CV",            fallbackInput: 5_000,  fallbackOutput: 1_100 },
  { code: "evaluate_job",       label: "Evaluate Job",        fallbackInput: 4_000,  fallbackOutput: 1_500 },
  { code: "tailor_cv",          label: "Tailor CV",           fallbackInput: 6_500,  fallbackOutput: 1_800 },
  { code: "refine_cv",          label: "Refine CV",           fallbackInput: 3_600,  fallbackOutput: 1_800 },
  { code: "cover_letter",       label: "Cover Letter",        fallbackInput: 2_300,  fallbackOutput: 300   },
  { code: "interview_prep",     label: "Interview Prep",      fallbackInput: 2_700,  fallbackOutput: 2_100 },
  { code: "mock_interview",     label: "Mock Interview",      fallbackInput: 2_050,  fallbackOutput: 230   },
  { code: "follow_up",          label: "Follow-up Email",     fallbackInput: 1_920,  fallbackOutput: 150   },
  { code: "rejection_analysis", label: "Rejection Analysis",  fallbackInput: 2_950,  fallbackOutput: 500   },
  { code: "negotiation_guide",  label: "Negotiation Guide",   fallbackInput: 1_050,  fallbackOutput: 750   },
  { code: "content_review",     label: "Content review",     fallbackInput: 4_500,  fallbackOutput: 700   },
  { code: "factual_review",     label: "Factual review",     fallbackInput: 4_000,  fallbackOutput: 400   },
  { code: "jd_compress",        label: "JD Compress",         fallbackInput: 3_300,  fallbackOutput: 900   },
  { code: "search_expand",      label: "Search Expand",       fallbackInput: 1_000,  fallbackOutput: 120   },
];

const VISION_OP: BenchmarkOp = {
  code: "review_cv", label: "Review CV", fallbackInput: 2_400, fallbackOutput: 250, isVision: true,
};

/**
 * Prices come from `MODEL_COST_MAP` in @winche/shared, the map the backend
 * estimates and budgets with (ux-admin-07): a hand-copied table here had
 * Gemini 2.5 Flash at the old 0.15 / 0.60.
 */
function withMapPrice(model: Omit<Model, "inputPer1M" | "outputPer1M"> & Partial<Pick<Model, "inputPer1M" | "outputPer1M">>): Model {
  const price = modelPricePer1M(model.id);
  if (price) return { ...model, inputPer1M: price.inputPer1M, outputPer1M: price.outputPer1M };
  // Off the map: the OpenRouter catalog price it was added with, if any.
  const known = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
  return { ...model, inputPer1M: known(model.inputPer1M), outputPer1M: known(model.outputPer1M) };
}

const DEFAULT_TEXT_MODELS: Model[] = [
  { id: "google/gemini-2.5-flash-lite",       label: "Gemini 2.5 Flash Lite", supportsVision: true  },
  { id: "google/gemini-2.5-flash",            label: "Gemini 2.5 Flash",      supportsVision: true  },
  { id: "google/gemini-flash-1.5-8b",         label: "Gemini 1.5 Flash 8B",   supportsVision: true  },
  { id: "anthropic/claude-haiku-4-5",         label: "Claude Haiku 4.5",      supportsVision: true  },
  { id: "anthropic/claude-haiku-5.5",         label: "Claude Haiku 5.5",      supportsVision: true  },
  { id: "openai/gpt-4o-mini",                 label: "GPT-4o mini",           supportsVision: true  },
  { id: "openai/gpt-4.1-nano",                label: "GPT-4.1 nano",          supportsVision: true  },
  { id: "meta-llama/llama-3.3-70b-instruct",  label: "Llama 3.3 70B",         supportsVision: false },
].map(withMapPrice);

const DEFAULT_VISION_MODELS: Model[] = DEFAULT_TEXT_MODELS.filter(m => m.supportsVision !== false);

// ─── Cost helpers ─────────────────────────────────────────────────────────────

function opCost(op: BenchmarkOp, model: Model, operationStats?: Partial<Record<AiOperation, OperationStat>>): number | null {
  if (model.inputPer1M == null || model.outputPer1M == null) return null;
  let inTok = op.fallbackInput;
  let outTok = op.fallbackOutput;
  const stat = operationStats?.[op.code];
  if (stat && stat.callCount > 0) {
    inTok  = stat.promptTokens / stat.callCount;
    outTok = stat.completionTokens / stat.callCount;
  }
  return (inTok / 1_000_000) * model.inputPer1M + (outTok / 1_000_000) * model.outputPer1M;
}

function fmtCost(n: number | null): string {
  if (n == null) return "price unknown";
  if (n >= 100)  return `$${n.toFixed(0)}`;
  if (n >= 10)   return `$${n.toFixed(1)}`;
  if (n >= 1)    return `$${n.toFixed(2)}`;
  if (n >= 0.01) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(4)}`;
}

function rowColors(costs: (number | null)[]): ("green" | "red" | null)[] {
  const known = costs.filter((c): c is number => c != null);
  if (known.length <= 1) return costs.map(() => null);
  const min = Math.min(...known), max = Math.max(...known);
  if (min === max) return costs.map(() => null);
  return costs.map(c => c === min ? "green" : c === max ? "red" : null);
}

/**
 * Cost per 100 users per month on `model`, from the text operations of the
 * plan mix. Null ("price unknown") when the model has no price: any
 * operation without one would silently drop out of the total.
 */
function tierCosts(
  model: Model,
  operationStats?: Partial<Record<AiOperation, OperationStat>>
): { free: number; search: number; radar: number; total: number } | null {
  const FREE_ACTIVE = 50 * 0.75;
  const SEARCH = 40;
  const RADAR = 10;
  function oc(code: AiOperation): number | null {
    const op = TEXT_OPERATIONS.find(o => o.code === code);
    return op ? opCost(op, model, operationStats) : null;
  }
  const eval_ = oc("evaluate_job");
  const tailor = oc("tailor_cv");
  const cover  = oc("cover_letter");
  const prep   = oc("interview_prep");
  if (eval_ == null || tailor == null || cover == null || prep == null) return null;
  const free   = FREE_ACTIVE * (4 * 50 * eval_ + tailor);
  const search = SEARCH * (33 * 150 * eval_ + 8 * tailor + 5 * cover + 3 * prep);
  const radar  = RADAR  * (4 * 50 * eval_ + 2 * tailor + prep);
  return { free, search, radar, total: free + search + radar };
}

function orModelToModel(m: OrModel): Model {
  return withMapPrice({ id: m.id, label: m.name, inputPer1M: m.inputPer1M, outputPer1M: m.outputPer1M, isCustom: true, supportsVision: m.supportsVision });
}

// ─── ModelPill ────────────────────────────────────────────────────────────────

function ModelPill({ model, isActive, canRemove, onSetActive, onRemove, setting, disabled, actionLabel = "Use this model" }: {
  model: Model;
  isActive: boolean;
  canRemove: boolean;
  onSetActive: () => void;
  onRemove: () => void;
  setting: boolean;
  disabled: boolean;
  actionLabel?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-1 rounded-md border py-0.5 pl-3 pr-1 text-label",
        isActive ? "border-brand bg-selected" : "border-border bg-card",
      )}
    >
      <span className={cn("flex items-center gap-1.5 py-1", isActive ? "text-foreground" : "text-muted-foreground")}>
        {model.label}
        {model.id.endsWith(":free") && (
          <Badge tone="success" size="sm">
            free
          </Badge>
        )}
        {model.isCustom && !model.id.endsWith(":free") && (
          <Badge tone="outline" size="sm">
            custom
          </Badge>
        )}
      </span>
      {isActive ? (
        <Badge tone="brand" size="sm" className="mx-1">
          Active
        </Badge>
      ) : (
        <Button variant="ghost" size="xs" onClick={onSetActive} loading={setting} disabled={disabled}>
          {actionLabel}
        </Button>
      )}
      {canRemove && (
        <IconButton label={`Remove ${model.label} from the comparison`} variant="ghost" size="icon-sm" onClick={onRemove}>
          <Icon icon={X} size="sm" />
        </IconButton>
      )}
    </div>
  );
}

/** Cheapest in green, most expensive in red: color only as a threshold. */
function costTone(color: "green" | "red" | null): string | undefined {
  return color === "green" ? "text-success font-medium" : color === "red" ? "text-danger-fg" : undefined;
}

// ─── Tier breakdown table ─────────────────────────────────────────────────────

type TierKey = "free" | "search" | "radar";
interface TierRow {
  key: TierKey | "total";
  label: string;
  note?: string;
}

function TierBreakdownTable({
  visibleModels,
  activeModelId,
  operationStats,
}: {
  visibleModels: Model[];
  activeModelId: string;
  operationStats?: Partial<Record<AiOperation, OperationStat>>;
}) {
  const allTiers = visibleModels.map((m) => tierCosts(m, operationStats));
  const colors: Record<TierRow["key"], ("green" | "red" | null)[]> = {
    free: rowColors(allTiers.map((t) => t?.free ?? null)),
    search: rowColors(allTiers.map((t) => t?.search ?? null)),
    radar: rowColors(allTiers.map((t) => t?.radar ?? null)),
    total: rowColors(allTiers.map((t) => t?.total ?? null)),
  };
  const rows: TierRow[] = [
    { key: "free", label: "Free users", note: "37 active × 4 scans × 50 evals + 1 tailor" },
    { key: "search", label: "PRO Search", note: "40 users × 33 scans × 150 evals + CVs, letters, preps" },
    { key: "radar", label: "PRO Radar", note: "10 users × 4 scans × 50 evals + 2 tailors" },
    { key: "total", label: "Total per 100 users" },
  ];
  const columns: Column<TierRow>[] = [
    {
      key: "tier",
      header: "Tier (per 100 users)",
      cell: (r) => (
        <span className="flex flex-col">
          <span className={r.key === "total" ? "font-semibold" : "font-medium"}>{r.label}</span>
          {r.note ? <span className="text-caption text-muted-foreground">{r.note}</span> : null}
        </span>
      ),
    },
    ...visibleModels.map((m, i) => ({
      key: m.id,
      header: (
        <span className="inline-flex items-center gap-1.5">
          {m.label}
          {m.id === activeModelId ? (
            <Badge tone="brand" size="sm">
              Active
            </Badge>
          ) : null}
        </span>
      ),
      align: "right" as const,
      cell: (r: TierRow) => {
        const cost = allTiers[i]?.[r.key] ?? null;
        return <span className={cost == null ? "text-subtle-foreground" : costTone(colors[r.key][i] ?? null)}>{fmtCost(cost)}</span>;
      },
    })),
  ];
  return <DataTable caption="AI cost per 100 users per month by model" columns={columns} rows={rows} rowKey={(r) => r.key} />;
}

// ─── Main section ─────────────────────────────────────────────────────────────

type PickerKind = "text" | "vision";

export function BenchmarkSection({
  activeModelId,
  activeVisionModelId,
  onSetActiveModel,
  onSetActiveVisionModel,
  operationStats,
  savedCustomModels,
  onSaveCustomModels,
}: {
  activeModelId: string;
  activeVisionModelId: string;
  paidFallbackModelId: string | null;
  paidFallbackVisionModelId: string | null;
  /** `acknowledgeFreeTierRetention`: the operator confirmed the free-tier warning (ux-admin-02). */
  onSetActiveModel: (modelId: string, acknowledgeFreeTierRetention?: boolean) => Promise<void>;
  onSetActiveVisionModel: (modelId: string, acknowledgeFreeTierRetention?: boolean) => Promise<void>;
  onSetPaidFallbackModel: (modelId: string | null) => Promise<void>;
  onSetPaidFallbackVisionModel: (modelId: string | null) => Promise<void>;
  operationStats?: Partial<Record<AiOperation, OperationStat>>;
  savedCustomModels: object;
  onSaveCustomModels: (textModels: object[], visionModels: object[]) => Promise<void>;
}) {
  type SavedShape = { text?: Model[]; vision?: Model[] };
  const saved = !Array.isArray(savedCustomModels) ? (savedCustomModels as SavedShape) : {};
  // A saved list keeps the prices it was saved with: listed models take the map's current price.
  const [textModels, setTextModels] = useState<Model[]>(() => (saved.text?.length ? saved.text.map(withMapPrice) : DEFAULT_TEXT_MODELS));
  const [visionModels, setVisionModels] = useState<Model[]>(() => (saved.vision?.length ? saved.vision.map(withMapPrice) : DEFAULT_VISION_MODELS));
  const [settingModel, setSettingModel] = useState<string | null>(null);
  const [settingVision, setSettingVision] = useState<string | null>(null);
  const [modelError, setModelError] = useState<AdminFailure | string | null>(null);
  const [activePicker, setActivePicker] = useState<PickerKind | null>(null);

  // Saves go one after another and only the latest change can revert the lists to what
  // config/global holds and show its error (ux-admin-09, lib/latest-save.ts).
  const saveCustomModels = useRef(onSaveCustomModels);
  useEffect(() => {
    saveCustomModels.current = onSaveCustomModels;
  });
  const saver = useRef<LatestSaver<ModelLists> | null>(null);
  function persist(nextText: Model[], nextVision: Model[]) {
    // Created on the first change, when the lists on screen are still the saved ones.
    saver.current ??= createLatestSaver<ModelLists>({
      initial: { text: textModels, vision: visionModels },
      write: (lists) => saveCustomModels.current(lists.text, lists.vision),
      onRevert: (lists) => {
        setTextModels(lists.text);
        setVisionModels(lists.vision);
      },
      onError: (e) => setModelError(describeCallableError("setBenchmarkCustomModels", e)),
    });
    setTextModels(nextText);
    setVisionModels(nextVision);
    setModelError(null);
    void saver.current.save({ text: nextText, vision: nextVision });
  }
  function addTextModel(m: OrModel) {
    persist([...textModels, orModelToModel(m)], visionModels);
    setActivePicker(null);
  }
  function removeTextModel(id: string) {
    persist(textModels.filter((m) => m.id !== id), visionModels);
  }
  function addVisionModel(m: OrModel) {
    persist(textModels, [...visionModels, orModelToModel(m)]);
    setActivePicker(null);
  }
  function removeVisionModel(id: string) {
    persist(textModels, visionModels.filter((m) => m.id !== id));
  }

  // «Use this model» moves every AI call of every user: it asks first, with the
  // scope, and writes the model it shows (m.id), never a `:free` variant
  // inherited from the active one (ux-admin-02). The last change can be reverted.
  const [pendingChange, setPendingChange] = useState<ModelChange | null>(null);
  const [lastChange, setLastChange] = useState<ModelChange | null>(null);

  function handleSetActive(modelId: string) {
    setModelError(null);
    setPendingChange({ type: "active", kind: "text", from: activeModelId || null, to: modelId });
  }

  function handleSetVisionActive(modelId: string) {
    const m = visionModels.find((v) => v.id === modelId);
    if (m?.supportsVision === false) {
      setModelError(`${m.label} does not support vision inputs.`);
      return;
    }
    setModelError(null);
    setPendingChange({ type: "active", kind: "vision", from: activeVisionModelId || null, to: modelId });
  }

  /** The confirmed change, written through its callable. A failure shows above the lists. */
  async function applyModelChange(change: ModelChange, acknowledgeFreeTierRetention: boolean) {
    if (change.type !== "active") return;
    const setting = change.kind === "text" ? setSettingModel : setSettingVision;
    setting(change.to);
    setModelError(null);
    try {
      if (change.kind === "text") await onSetActiveModel(change.to, acknowledgeFreeTierRetention);
      else await onSetActiveVisionModel(change.to, acknowledgeFreeTierRetention);
      setLastChange(change);
    } catch (e) {
      setModelError(describeCallableError(change.kind === "text" ? "setActiveModel" : "setActiveVisionModel", e));
    } finally {
      setting(null);
    }
  }

  const activeModelBase = activeModelId.replace(/:free$/, "");
  const activeVisionModelBase = activeVisionModelId.replace(/:free$/, "");
  const activeModel = textModels.find((m) => m.id === activeModelBase);
  const activeVisionModel = visionModels.find((m) => m.id === activeVisionModelBase);
  const hasRealData = operationStats && Object.values(operationStats).some((s) => s && s.callCount > 0);

  const visionStat = operationStats?.[VISION_OP.code];
  const visionReal = Boolean(visionStat && visionStat.callCount > 0);
  const visionModelsForTable = visionModels.filter((m) => !m.id.endsWith(":free"));
  const visionPerCall = visionModelsForTable.map((m) => opCost(VISION_OP, m, operationStats));
  const visionColors = rowColors(visionPerCall);

  const modelList = (kind: PickerKind) => {
    const list = kind === "text" ? textModels : visionModels;
    // The exact active id: with a `:free` model active, its paid base is another model to pick.
    const activeId = kind === "text" ? activeModelId : activeVisionModelId;
    const settingId = kind === "text" ? settingModel : settingVision;
    return (
      <div className="flex flex-wrap items-center gap-2">
        {list.map((m) => (
          <ModelPill
            key={m.id}
            model={m}
            isActive={m.id === activeId}
            canRemove={m.id !== activeId && list.length > 1}
            onSetActive={() => (kind === "text" ? handleSetActive(m.id) : handleSetVisionActive(m.id))}
            onRemove={() => (kind === "text" ? removeTextModel(m.id) : removeVisionModel(m.id))}
            setting={settingId === m.id}
            disabled={settingId !== null}
          />
        ))}
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            className="border-dashed"
            leadingIcon={<Icon icon={Plus} size="sm" />}
            onClick={() => setActivePicker((v) => (v === kind ? null : kind))}
            aria-expanded={activePicker === kind}
          >
            Add model
          </Button>
          {activePicker === kind && (
            <ModelSearch
              existingIds={new Set(list.map((m) => m.id))}
              visionOnly={kind === "vision"}
              onSelect={kind === "text" ? addTextModel : addVisionModel}
              onClose={() => setActivePicker(null)}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <SectionHeader
        title="Benchmark"
        subtitle="Compare estimated LLM costs at the retail prices the backend uses (MODEL_COST_MAP), then pick the model to use. A model off the map keeps its OpenRouter catalog price, or shows price unknown."
      />

      {modelError && (typeof modelError === "string" ? <ErrorMessage message={modelError} /> : <ErrorMessage failure={modelError} />)}
      {lastChange && (
        <ModelChangeNotice
          change={lastChange}
          onRevert={setPendingChange}
          disabled={settingModel !== null || settingVision !== null || pendingChange !== null}
        />
      )}
      <ModelChangeDialog change={pendingChange} onCancel={() => setPendingChange(null)} onConfirm={applyModelChange} />

      <Callout tone="neutral" className="mb-6">
        The free model, paid fallback and reasoning live in{" "}
        <a href="#settings" className="text-link underline underline-offset-4">
          Settings
        </a>
        . Here you compare and choose the base model.
      </Callout>

      <Block title="Text models" hint="Every operation except Review CV.">
        <Panel>
          <p className="mb-3 text-label text-muted-foreground">
            Active: <span className="font-medium text-foreground">{activeModel?.label ?? activeModelBase}</span>
            {isFreeModelId(activeModelId) && (
              <Badge tone="success" size="sm" className="ml-2">
                free tier
              </Badge>
            )}
          </p>
          {modelList("text")}
        </Panel>
      </Block>

      <Block
        title="AI cost per 100 users per month"
        hint="Mix: 50 free (75% active), 40 PRO Search, 10 PRO Radar. Text operations only: Review CV has its own table below. A model without a price shows price unknown."
        action={
          hasRealData ? (
            <Badge tone="success" size="sm">
              Real average tokens
            </Badge>
          ) : (
            <Badge tone="neutral" size="sm">
              Estimated tokens
            </Badge>
          )
        }
      >
        <TierBreakdownTable
          visibleModels={textModels.filter((m) => !m.id.endsWith(":free"))}
          activeModelId={activeModelBase}
          operationStats={operationStats}
        />
      </Block>

      <Block title="Vision models" hint="Review CV only (reads the PDF or image).">
        <Panel>
          <p className="mb-3 text-label text-muted-foreground">
            Active: <span className="font-medium text-foreground">{activeVisionModel?.label ?? activeVisionModelBase}</span>
            {isFreeModelId(activeVisionModelId) && (
              <Badge tone="success" size="sm" className="ml-2">
                free tier
              </Badge>
            )}
          </p>
          {modelList("vision")}
        </Panel>
      </Block>

      {visionModelsForTable.length > 0 && (
        <Block
          title="Review CV cost per call"
          action={
            <Badge tone={visionReal ? "success" : "neutral"} size="sm">
              {visionReal
                ? `${Math.round(visionStat!.promptTokens / visionStat!.callCount).toLocaleString("en-US")} / ${Math.round(visionStat!.completionTokens / visionStat!.callCount).toLocaleString("en-US")} real tokens`
                : `${VISION_OP.fallbackInput.toLocaleString("en-US")} / ${VISION_OP.fallbackOutput.toLocaleString("en-US")} estimated tokens`}
            </Badge>
          }
        >
          <DataTable
            caption="Review CV cost per call by model"
            rows={visionModelsForTable.map((m, i) => ({ m, cost: visionPerCall[i] ?? null, color: visionColors[i] ?? null }))}
            rowKey={(r) => r.m.id}
            defaultSort={{ key: "cost", dir: "asc" }}
            columns={[
              {
                key: "model",
                header: "Model",
                cell: (r) => (
                  <span className="flex items-center gap-1.5">
                    {r.m.label}
                    {r.m.id === activeVisionModelBase && (
                      <Badge tone="brand" size="sm">
                        Active
                      </Badge>
                    )}
                  </span>
                ),
              },
              {
                key: "cost",
                header: "Cost per call",
                align: "right",
                cell: (r) => <span className={r.cost == null ? "text-subtle-foreground" : costTone(r.color)}>{fmtCost(r.cost)}</span>,
                sortValue: (r) => r.cost,
              },
            ]}
          />
        </Block>
      )}
    </div>
  );
}
